import React, {useState} from "react";
import {Box, Stack, Typography, IconButton, Button, Modal, Fade, CircularProgress} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {Cancel01Icon, SparklesIcon} from "@hugeicons/core-free-icons";
import {toast} from "react-toastify";
import {KnowledgeManagerRepository} from "../../../../repositories/KnowledgeManagerRepository";
import {SplitMarkdownEditor} from "../../../../components/markdown/SplitMarkdownEditor";
import {momentHash} from "../../../../utils/momentHash";

export const CaptureKnowledgeModalNew = ({topicId, open, onClose, onCaptured}) => {
  const [content, setContent] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleClose = () => {
    if (submitting) return;
    setContent("");
    onClose();
  };

  const handleCapture = async () => {
    if (!content.trim()) return;
    setSubmitting(true);
    try {
      await KnowledgeManagerRepository.uploadTextDocument(topicId, {
        content, fileName: `captured-${momentHash()}.md`,
      });
      toast.success("Added to the knowledge base");
      setContent("");
      onCaptured?.();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to capture knowledge");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Fade in={open}>
        <Box sx={{
          position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
          width: {xs: "calc(100% - 24px)", md: "min(1000px, 94vw)"}, height: {xs: "calc(100% - 24px)", md: "80vh"},
          display: "flex", flexDirection: "column", overflow: "hidden", outline: "none",
          bgcolor: '#fff', borderRadius: '20px', boxShadow: '0 24px 70px rgba(35, 38, 77, 0.22)', p: {xs: 2.5, md: 3},
        }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{mb: 2.5}}>
            <Stack direction="row" alignItems="center" gap={1.25}>
              <Box sx={{
                width: 40, height: 40, borderRadius: "12px", bgcolor: "rgba(132, 145, 247, 0.12)", flexShrink: 0,
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <HugeiconsIcon icon={SparklesIcon} size={18} color={"#8491F7"}/>
              </Box>
              <Box>
                <Typography sx={{fontSize: 16, fontWeight: 700, color: '#23264D'}}>Quick Note</Typography>
                <Typography sx={{fontSize: 12, color: '#8A8AA0', mt: 0.25}}>Adds a new document to this topic's knowledge base</Typography>
              </Box>
            </Stack>
            <IconButton
              onClick={handleClose}
              disabled={submitting}
              sx={{width: 34, height: 34, borderRadius: '10px', border: '1px solid #ECECF3', color: '#8A8AA0', flexShrink: 0}}
            >
              <HugeiconsIcon icon={Cancel01Icon} size={18}/>
            </IconButton>
          </Stack>

          <Box sx={{flex: 1, minHeight: 0}}>
            <SplitMarkdownEditor
              value={content}
              onChange={setContent}
              placeholder={"Write what should be added to the knowledge base…"}
              ariaLabel={"Capture knowledge content"}
            />
          </Box>

          <Stack direction="row" gap={1.5} justifyContent="flex-end" sx={{mt: 2}}>
            <Button
              onClick={handleClose}
              disabled={submitting}
              sx={{
                textTransform: 'none', fontWeight: 600, fontSize: 13, borderRadius: '10px',
                color: '#374151', bgcolor: '#FAFAFD', '&:hover': {bgcolor: '#ECECF3'},
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCapture}
              disabled={!content.trim() || submitting}
              startIcon={submitting
                ? <CircularProgress size={14} sx={{color: "#4B55C9"}}/>
                : <HugeiconsIcon icon={SparklesIcon} size={15}/>}
              sx={{
                textTransform: 'none', fontWeight: 600, fontSize: 13, borderRadius: '10px',
                bgcolor: 'rgba(132, 145, 247, 0.12)', color: '#4B55C9', '&:hover': {bgcolor: 'rgba(132, 145, 247, 0.2)'},
                '&.Mui-disabled': {bgcolor: '#F0F0F6', color: '#B4B4C3'},
              }}
            >
              {submitting ? "Adding…" : "Add Note"}
            </Button>
          </Stack>
        </Box>
      </Fade>
    </Modal>
  );
};

export default CaptureKnowledgeModalNew;
